import {
  type PrescriptionSave,
  useCreatePrescription
} from '@features/fields/api/useCreatePrescription'
import { usePrescriptionAuroraTools } from '@features/fields/hooks/usePrescriptionAuroraTools'
import { AuroraControl } from '@features/misc/auroraControls/AuroraControl'
import { AwaitKind, auroraControls } from '@features/misc/auroraControls/auroraControlsStore'
import { type ButtonProps, Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { AuroraControlId, type PrescriptionProduct } from 'api-validations/aurora'
import { PlanAssetCreateError } from 'api-validations/locutus'
import {
  applicationTypesToUnits,
  controllableApplicationTypes
} from 'api-validations/stitch/applications'
import { Fragment, type PropsWithChildren, useEffect, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import {
  ButtonKind,
  CircleSpinner,
  IconRotation,
  MeatballMenu,
  MeatballMenuItem,
  PanoramaButton,
  PanoramaSelectMenu,
  TrashIcon
} from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import {
  createZone,
  getZoneById,
  resetZoneEditing,
  restoreZones,
  useCurrentZones,
  useMapContext
} from 'shared-ui/map'
import { Geo, isNil } from 'utils'

import {
  Attributes,
  type FormValues,
  RateGroups,
  type Unit,
  type Zone
} from './PrescriptionDetailsTab'
import { PRESCRIPTION_BASE_ZONE_ID, ToolsAndShapes } from './ReusedComponents'

type SavedPrescription = Extract<PrescriptionSave, { saved: true }>['prescription']

const rateOrZero = (rate: number | undefined) =>
  rate === undefined || Number.isNaN(rate) ? 0 : rate

export const PrescriptionDrawing = ({
  defaultZones,
  defaultUnits,
  defaultProducts = [],
  returnToMain,
  fieldId
}: {
  defaultUnits?: Unit[]
  defaultZones?: Zone[]
  defaultProducts?: PrescriptionProduct[]
  returnToMain: (
    prescriptions?: {
      id: string
      name: string
    }[]
  ) => void
  fieldId: string
}) => {
  const { map } = useMapContext()
  const { t, localizeUnit, measureSystem, formatMetric } = useLocalization()

  const controllableApplicationOptions = controllableApplicationTypes.options.map((name) => ({
    unit: `${localizeUnit(applicationTypesToUnits[name][measureSystem])}`,
    type: name,
    name: t(($) => $.agConcepts.applicationTypes[name])
  }))
  const blankUnit = (product: PrescriptionProduct | null = null): Unit => ({
    type:
      controllableApplicationOptions.find(({ type }) => type === product?.applicationType) ??
      controllableApplicationOptions[0],
    name: product?.name ?? '',
    id: '',
    attributes: (product?.rateColumns ?? ['']).map((name) => ({ name, zones: {} }))
  })

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [areas, setAreas] = useState<{
    area: number
    zones: Record<string, number>
  }>(() => {
    const { covered, zones } = Geo.getAreas(map.drawTool.getFeatureCollection())
    return { area: covered, zones }
  })

  useEffect(() => {
    const { cleanup } = map.drawTool.onChange((collection) => {
      const { covered, zones } = Geo.getAreas(collection)
      setAreas({ area: covered, zones })
    })

    return cleanup
  }, [map.drawTool])

  useEffect(() => {
    if (isNil(defaultZones)) {
      const existingBaseZone = getZoneById(PRESCRIPTION_BASE_ZONE_ID)
      if (isNil(existingBaseZone)) {
        createZone({
          id: PRESCRIPTION_BASE_ZONE_ID,
          name: t(($) => $.planning.boundaryCreation.baseRate)
        })
      }
      createZone({ name: `${t(($) => $.labels.zone, { count: 1 })} 2` })
    } else {
      restoreZones(defaultZones)
    }
    return resetZoneEditing
  }, [])

  const {
    control,
    getValues,
    handleSubmit,
    setFocus,
    setValue,
    trigger,
    formState: { errors }
  } = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      units: defaultUnits ?? defaultProducts.map((product) => blankUnit(product))
    }
  })
  const {
    fields: units,
    append,
    remove: removeUnit
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'units' // unique name for your Field Array
  })

  const describeSave = (save: PrescriptionSave) => {
    if (save.saved) return t(($) => $.auroraControls.values.saved, { name: save.prescription.name })
    const { name, error } = save
    const reason =
      error.type === PlanAssetCreateError.DuplicateName
        ? t(($) => $.planning.duplicateAssetName.Prescription)
        : error.type === PlanAssetCreateError.TooLarge
          ? t(($) => $.planning.assetTooLargeToSave)
          : error.message || t(($) => $.messages.errorOccurredTitle)
    return t(($) => $.planning.couldNotSavePrescription, { name, reason })
  }

  const saved = useRef<SavedPrescription[]>([])
  const save = useCreatePrescription((saves) => {
    auroraControls.report({
      control: AuroraControlId.SavePrescription,
      report: saves.map(describeSave).join(' ')
    })
    saved.current.push(...saves.flatMap((save) => (save.saved ? [save.prescription] : [])))
    if (saves.some((save) => !save.saved)) {
      removeUnit(saves.flatMap((save, index) => (save.saved ? [index] : [])))
      return
    }
    map.drawTool.disable()
    returnToMain(saved.current)
    map.drawTool.clear()
  })
  const failedSaves = (save.data ?? []).filter(({ saved }) => !saved)

  usePrescriptionAuroraTools({
    getValues,
    setValue,
    trigger,
    addProduct: (product) => append(blankUnit(product)),
    unitTypes: controllableApplicationOptions,
    areas
  })
  const watchedUnits = useWatch({ control, name: 'units' })
  const zones = [...(useCurrentZones()?.zones.values() ?? [])]
  const ratesValue = zones
    .map((zone) =>
      t(($) => $.auroraControls.values.rate, {
        zone: zone.name,
        rate: watchedUnits
          .flatMap(({ name: product, attributes, type }) =>
            attributes.map(
              ({ name: column, zones: rates }) =>
                `${watchedUnits.length > 1 ? `${product} ` : ''}${column} ${formatMetric({ value: rateOrZero(rates[zone.id]) })} ${type.unit}`
            )
          )
          .join(', ')
      })
    )
    .join('\n')

  const onSave = () => {
    save.mutate(
      getValues('units').map((unit) => ({
        applicationType: unit.type.type,
        measureSystem: measureSystem,
        fieldId,
        name: unit.name,
        attributes: unit.attributes
      }))
    )
  }

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
        <TabList className="flex w-full justify-evenly rounded-lg border border-gray-300 bg-white p-2">
          <StyledTab>{t(($) => $.labels.details)}</StyledTab>
          <StyledTab
            className="disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-gray-300"
            onClick={async (event) => {
              event.preventDefault()
              if (units.length > 0 && (await trigger())) {
                setSelectedIndex(1)
              }
            }}
          >
            {t(($) => $.titles.rateZones)}
          </StyledTab>
        </TabList>
        <TabPanels>
          <TabPanel unmount={false}>
            <div className="flex w-full flex-col gap-2 py-4">
              <span className="border-b border-gray-300 pb-4 text-sm text-gray-600">
                {t(($) => $.planning.boundaryCreation.gettingStartedPrescription)}
              </span>
            </div>
            <div className="flex flex-col gap-2 pb-4">
              <div className="flex items-center justify-between">
                <span>{t(($) => $.labels.unit, { count: 2 })}</span>
                <PanoramaButton
                  type="button"
                  kind={ButtonKind.Secondary}
                  className="text-sm"
                  icon={SpriteMap.AddCircleIcon}
                  onClick={async () => {
                    append(blankUnit())
                    await trigger('units')
                  }}
                >
                  {t(($) => $.buttons.addUnit)}
                </PanoramaButton>
              </div>
              {units.length < 1 && (
                <div className="text-sm text-red-600">{t(($) => $.planning.atLeastOne)}</div>
              )}
            </div>
            <fieldset>
              {units.map((unit, index) => (
                <div
                  key={unit.id}
                  className="relative flex w-full flex-col gap-2 rounded-lg border border-gray-300 p-4"
                >
                  <div className="absolute right-4 top-4">
                    <MeatballMenu menuItemsWidth="w-fit">
                      <MeatballMenuItem
                        color="danger"
                        icon={<TrashIcon height="h-5" color="text-inherit" />}
                        title={t(($) => $.remove)}
                        onClick={() => removeUnit(index)}
                      />
                    </MeatballMenu>
                  </div>
                  <Controller
                    control={control}
                    name={`units.${index}.type`}
                    render={({ field }) => (
                      <PanoramaSelectMenu
                        nameAccessor={({ type }) => type}
                        {...field}
                        label={t(($) => $.labels.unit, { count: 1 })}
                        options={controllableApplicationOptions}
                        idAccessor={({ type }) => type}
                        nameFormatter={({ unit, name }) => (
                          <div className="flex items-baseline gap-1">
                            <span className="font-semibold capitalize">{unit}</span>
                            <span className="text-xs">({name})</span>
                          </div>
                        )}
                      />
                    )}
                  />

                  <Attributes control={control} index={index} />
                </div>
              ))}
            </fieldset>

            <div className="flex gap-4 py-4">
              <PanoramaButton
                type="button"
                onClick={() => {
                  map.drawTool.clear()
                  returnToMain(saved.current.length > 0 ? saved.current : undefined)
                }}
                kind={ButtonKind.Secondary}
                icon={SpriteMap.CloseCircleIcon}
                className="w-full"
              >
                {t(($) => $.buttons.cancel)}
              </PanoramaButton>
              <PanoramaButton
                type="button"
                className="w-full"
                icon={SpriteMap.ChevronIcon}
                iconRotation={IconRotation.Deg90}
                onClick={async () => {
                  if (units.length > 0 && (await trigger())) setSelectedIndex(1)
                }}
              >
                {t(($) => $.buttons.next)}
              </PanoramaButton>
            </div>
          </TabPanel>
          <TabPanel unmount={false}>
            <AuroraControl id={AuroraControlId.ZoneRates} value={ratesValue}>
              <RateGroups setFocus={setFocus} control={control} setValue={setValue} areas={areas} />
            </AuroraControl>
            {/* ToolsAndShapes conditionally renders with the tab; RateGroups and the save button stay mounted so Aurora can show them from either tab */}
            {selectedIndex === 1 && (
              <div className="border-y border-gray-300 py-4">
                <ToolsAndShapes type="prescription" />
              </div>
            )}
            <div className="flex gap-4 pt-4">
              <PanoramaButton
                kind={ButtonKind.Secondary}
                type="button"
                icon={SpriteMap.ChevronIcon}
                iconRotation={IconRotation.Deg270}
                iconLeft
                onClick={() => setSelectedIndex(0)}
                className="w-full border-gray-300"
              >
                {t(($) => $.buttons.back)}
              </PanoramaButton>
              <AuroraControl
                id={AuroraControlId.SavePrescription}
                value={t(($) => $.auroraControls.values.notSaved)}
                awaits={{ kind: AwaitKind.Press }}
              >
                <div className="flex w-full flex-col gap-2">
                  <PanoramaButton
                    kind={
                      save.isError || failedSaves.length > 0
                        ? ButtonKind.Danger
                        : ButtonKind.Primary
                    }
                    type="button"
                    onClick={handleSubmit(onSave)}
                    disabledWhen={[
                      { when: units.length === 0, reason: t(($) => $.planning.atLeastOne) },
                      {
                        when: areas.area === 0,
                        reason: t(($) => $.planning.boundaryCreation.drawZonesFirst)
                      },
                      {
                        when: Object.keys(errors).length > 0,
                        reason: t(($) => $.disabledReasons.fillInRequiredFields)
                      }
                    ]}
                    className="w-full"
                    icon={SpriteMap.CheckCircleIcon}
                  >
                    {t(($) => $.planning.boundaryCreation.savePrescription)}
                    {save.isPending && <CircleSpinner />}
                  </PanoramaButton>
                  {save.isError && (
                    <p>{save.error.message || t(($) => $.messages.errorOccurredTitle)}</p>
                  )}
                  {failedSaves.map((failed, index) => (
                    <p key={index}>{describeSave(failed)}</p>
                  ))}
                </div>
              </AuroraControl>
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </form>
  )
}

const titleButtonClasses = createVariants({
  base: 'pan-title5 w-full py-2.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800 rounded',
  variants: {
    selected: {
      true: 'bg-accent-100 text-green-700 font-bold'
    }
  }
})

const StyledTab: React.FC<PropsWithChildren & ButtonProps> = ({ children, ...rest }) => {
  return (
    <Tab {...rest} as={Fragment}>
      {({ selected }) => (
        <button type="button" className={titleButtonClasses({ selected })}>
          {children}
        </button>
      )}
    </Tab>
  )
}
